import loadable from '@loadable/component';
import React from 'react';

// Lazy-load the component and disable SSR
const SnackbarContainer = loadable(
  () => import('@components/Snackbar/SnackbarContainer'),
  {
    ssr: false,
  }
);

const MAX_SNACKBARS = 2;

export type SnackbarVariant = 'error' | 'info' | 'success' | 'warning';

type SnackbarType = {
  variant: SnackbarVariant;
  message: React.ReactNode;
  id: number;
  isPersistent?: boolean;
};

type SnackbarStateType = {
  snackbars: SnackbarType[];
  counter: number;
};

export enum SnackbarActions {
  Enqueue = 'enqueue',
  Dequeue = 'dequeue',
}

type Payload = {
  [SnackbarActions.Dequeue]: { id: number };
  [SnackbarActions.Enqueue]: {
    message: React.ReactNode;
    variant: SnackbarVariant;
    isPersistent?: boolean;
  };
};

const initialSnackbarState: SnackbarStateType = {
  snackbars: [],
  counter: 0,
};

function reducer(state: SnackbarStateType, action: Action<Payload>) {
  switch (action.type) {
    case SnackbarActions.Enqueue:
      return {
        ...state,
        counter: state.counter + 1,
        snackbars: [
          ...state.snackbars.splice(
            state.snackbars.length - MAX_SNACKBARS - 1,
            state.snackbars.length
          ),
          {
            variant: action.variant,
            message: action.message,
            isPersistent: action.isPersistent,
            id: state.counter,
          },
        ],
      };
    case SnackbarActions.Dequeue:
      return {
        ...state,
        snackbars: state.snackbars.filter((bar) => bar.id !== action.id),
      };
    default:
      return state;
  }
}

export const SnackbarContext = React.createContext<{
  state: SnackbarStateType;
  dispatch: React.Dispatch<Action<Payload>>;
}>({ state: initialSnackbarState, dispatch: () => null });
SnackbarContext.displayName = 'SnackbarContext';

export const SnackbarProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children = null }) => {
  const [state, dispatch] = React.useReducer(reducer, initialSnackbarState);

  const stateKey = JSON.stringify(state);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const value = React.useMemo(() => ({ state, dispatch }), [stateKey]);

  return (
    <SnackbarContext.Provider value={value}>
      <SnackbarContainer />
      {children}
    </SnackbarContext.Provider>
  );
};
